import { isAbsoluteTimeRange, TimeRangeValue } from '@chronosphereio/core';
import { useCallback, useMemo } from 'react';
import {
  ChronospherePanelDefinition,
  FormulaDefinition,
  PanelDefinition,
  QueryResolution,
  TimeSeriesQueryDefinition,
} from '@/core';
import { toQueryProviderFormat } from '@/dashboards/utils';
import {
  AppMetadataProvider,
  DataQueriesProvider,
  DataQueriesProviderProps,
  usePanelPluginByDefinition,
  useReplaceVariablesInString,
  useSuggestedStepMs,
  useTimeRange,
} from '@/plugin-system';
import { TimeOverrideProvider } from '../../context';
import { BuiltInVariablesProvider } from './BuiltInVariablesProvider';

const EMPTY_QUERIES: TimeSeriesQueryDefinition[] = [];
const EMPTY_FORMULAS: FormulaDefinition[] = [];

export type PanelProviderProps = {
  /**
   * The panel definition.
   */
  panelDefinition: PanelDefinition;
  /**
   * The key of the panel.
   */
  panelKey?: string;
  /**
   * Disable queries from being executed.
   */
  disableQueries?: boolean;
  /**
   * Whether the panel is being viewed in edit panel dialog.
   */
  isEditPanel?: boolean;
  /**
   * The children to render.
   */
  children: React.ReactNode;
};

/**
 * Provider that sets up core panel features to avoid duplicating logic between dashboard and view/edit dialogs.
 * When relative time query option is set, a nested TimeRangeProvider is needed to override dashboard time.
 */
export function PanelProvider(props: PanelProviderProps) {
  const { panelDefinition, isEditPanel, children } = props;

  const { setTimeRange } = useTimeRange();

  const handleTimeRangeChange = useCallback(
    (newTimeRange: TimeRangeValue) => {
      // If newTimeRange is absolute, use it (even if panel has relative time override).
      // Needed to update the parent dashboard-level time range.
      if (isAbsoluteTimeRange(newTimeRange)) {
        setTimeRange(newTimeRange);
      }
    },
    [setTimeRange]
  );

  // Variables supported in resolution query option
  const panelQueryOptions = panelDefinition.spec.query_options;
  const resolution = useReplaceVariablesInString(panelQueryOptions?.resolution);

  // TimeOverrideProvider is already included in PanelEditorForm, this check ensures TimeRangeProvider
  // is not nested twice, which would cause queries to fire twice since timeRange is in the data queries key.
  if (isEditPanel) {
    return (
      <PanelProviderCore {...props} resolution={resolution}>
        {children}
      </PanelProviderCore>
    );
  }

  // Only wrap for view mode dashboard-level panels. TimeOverrideProvider checks whether query_options.time
  // is valid and determines whether to nest a TimeRangeProvider / how recomputeKey is calculated.
  return (
    <TimeOverrideProvider panelDefinition={panelDefinition} onTimeRangeChange={handleTimeRangeChange}>
      <PanelProviderCore {...props} resolution={resolution}>
        {children}
      </PanelProviderCore>
    </TimeOverrideProvider>
  );
}

type PanelProviderCoreProps = PanelProviderProps & {
  /**
   * Resolution to pass to DataQueriesProvider.
   * Ensures variables in resolution are replaced before calling useSuggestedStepMs.
   */
  resolution?: QueryResolution;
};

/**
 * Internal setup for core panel features (queries, variables, metadata, etc.)
 */
function PanelProviderCore(props: PanelProviderCoreProps) {
  const { panelDefinition, panelKey, children, disableQueries = false, resolution } = props;

  const { plugin, dataType, isLoading: isPanelPluginLoading } = usePanelPluginByDefinition(panelDefinition);

  const queries = panelDefinition.spec.queries;

  // Use an empty array of queries in the following cases:
  // - When queries in the panel definition are undefined.
  // - When the panel plugin hides the query editor (and thus does not support queries). A plugin
  //   that does not support queries can sometimes have them defined because of switching between
  //   different visualization types in the panel editor.
  const queryDefinitions = plugin?.hideQueryEditor || !queries ? EMPTY_QUERIES : queries;
  //  Disable query until panel plugin is loaded so we have the correct supported data type
  const queriesEnabled = !(disableQueries || isPanelPluginLoading);

  const definitions = useMemo(() => {
    return toQueryProviderFormat(queryDefinitions);
  }, [queryDefinitions]);

  const metadata = useMemo(() => {
    return {
      panelKey,
    };
  }, [panelKey]);

  // TODO: Extract this out as a value in the panel plugin setup config rather than hard-coding it.
  //
  // If the panel is a TimeSeriesChart with a bar display or a StatusHistory visualization, we adjust the
  // resolution to 0.25 so the step size is bigger and the bars are wider.
  let resolutionMultiplier = 1;
  if (
    (panelDefinition.spec.plugin.kind === 'TimeSeriesChart' &&
      panelDefinition.spec.plugin.spec?.visual?.display === 'bar') ||
    panelDefinition.spec.plugin.kind === 'StatusHistory'
  ) {
    resolutionMultiplier = 0.25;
  }

  const suggestedStepMs = useSuggestedStepMs({
    resolution,
    resolutionMultiplier,
  });

  const dataQueriesOptions: DataQueriesProviderProps['options'] = useMemo(() => {
    return {
      panelDefinition: panelDefinition as ChronospherePanelDefinition,
      suggestedStepMs,
      resolution,
      dataType,
      allowCompare: plugin?.enableCompareMode,
      queryMode: queriesEnabled ? 'active' : 'suspended',
    };
  }, [suggestedStepMs, resolution, plugin?.enableCompareMode, dataType, panelDefinition, queriesEnabled]);

  return (
    <AppMetadataProvider metadata={metadata}>
      <BuiltInVariablesProvider suggestedStepMs={suggestedStepMs}>
        <DataQueriesProvider
          queries={definitions}
          options={dataQueriesOptions}
          queryOptions={{ enabled: queriesEnabled }}
          formulas={panelDefinition.spec.formulas ?? EMPTY_FORMULAS}
          id={panelKey}
        >
          {children}
        </DataQueriesProvider>
      </BuiltInVariablesProvider>
    </AppMetadataProvider>
  );
}
